// Range value object with auto-correction
// Ensures min is always <= max by auto-swapping invalid ranges

export class Range {
  readonly min: number | null;
  readonly max: number | null;

  private constructor(min: number | null, max: number | null) {
    // Auto-correct: if min > max, swap them
    if (min !== null && max !== null && min > max) {
      this.min = max;
      this.max = min;
    } else {
      this.min = min;
      this.max = max;
    }
  }

  /**
   * Factory method to create a Range with auto-correction
   * If min > max, they will be automatically swapped
   */
  static create(min: number | null, max: number | null): Range {
    return new Range(min, max);
  }

  /**
   * Create Range from plain object (for compatibility with existing code)
   */
  static fromObject(obj: { min: number | null; max: number | null } | null | undefined): Range {
    if (!obj) return new Range(null, null);
    return new Range(obj.min, obj.max);
  }

  /**
   * Convert to plain object (for API/serialization)
   */
  toObject(): { min: number | null; max: number | null } {
    return { min: this.min, max: this.max };
  }

  /**
   * Check if range is empty (both min and max are null)
   */
  isEmpty(): boolean {
    return this.min === null && this.max === null;
  }

  /**
   * Update min value, returns new Range with auto-correction
   */
  withMin(min: number | null): Range {
    return new Range(min, this.max);
  }

  /**
   * Update max value, returns new Range with auto-correction
   */
  withMax(max: number | null): Range {
    return new Range(this.min, max);
  }
}
